class LogoTicker extends HTMLElement {
  constructor() {
    super();

    this.animationFrame = null;
    this.resizeFrame = null;
    this.lastFrameTime = 0;
    this.offset = 0;
    this.groupWidth = 0;
    this.isHovered = false;
    this.hasFocus = false;
    this.isVisible = true;

    this.handleFrame = this.handleFrame.bind(this);
    this.scheduleMeasure = this.scheduleMeasure.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  connectedCallback() {
    if (this.isInitialized) return;

    this.track = this.querySelector("[data-logo-ticker-track]");
    this.originalGroup = this.querySelector("[data-logo-ticker-original]");
    this.viewport = this.querySelector("[data-logo-ticker-viewport]");

    if (!this.track || !this.originalGroup || !this.viewport) return;

    this.isInitialized = true;
    this.speed = Number.parseFloat(this.dataset.speed) || 12;
    this.pauseOnHover = this.dataset.pauseOnHover === "true";
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    this.resizeObserver = new ResizeObserver(this.scheduleMeasure);
    this.resizeObserver.observe(this.viewport);
    this.resizeObserver.observe(this.originalGroup);

    this.intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.isVisible = entry?.isIntersecting ?? true;
        this.updateAnimation();
      },
      { rootMargin: "100px" },
    );
    this.intersectionObserver.observe(this);

    this.handleReducedMotionChange = () => this.scheduleMeasure();
    this.reducedMotion.addEventListener(
      "change",
      this.handleReducedMotionChange,
    );

    this.handleMouseEnter = () => {
      this.isHovered = true;
      this.updateAnimation();
    };
    this.handleMouseLeave = () => {
      this.isHovered = false;
      this.updateAnimation();
    };
    this.handleFocusIn = () => {
      this.hasFocus = true;
      this.updateAnimation();
    };
    this.handleFocusOut = (event) => {
      if (this.contains(event.relatedTarget)) return;
      this.hasFocus = false;
      this.updateAnimation();
    };

    this.addEventListener("mouseenter", this.handleMouseEnter);
    this.addEventListener("mouseleave", this.handleMouseLeave);
    this.addEventListener("focusin", this.handleFocusIn);
    this.addEventListener("focusout", this.handleFocusOut);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);

    this.querySelectorAll("img").forEach((image) => {
      if (!image.complete) {
        image.addEventListener("load", this.scheduleMeasure, { once: true });
      }
    });

    this.measure();
  }

  disconnectedCallback() {
    if (!this.isInitialized) return;

    this.stopAnimation();
    cancelAnimationFrame(this.resizeFrame);
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.reducedMotion?.removeEventListener(
      "change",
      this.handleReducedMotionChange,
    );
    this.removeEventListener("mouseenter", this.handleMouseEnter);
    this.removeEventListener("mouseleave", this.handleMouseLeave);
    this.removeEventListener("focusin", this.handleFocusIn);
    this.removeEventListener("focusout", this.handleFocusOut);
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
    );
    this.isInitialized = false;
  }

  createClone() {
    const clone = this.originalGroup.cloneNode(true);
    clone.removeAttribute("data-logo-ticker-original");
    clone.setAttribute("data-logo-ticker-clone", "");
    clone.setAttribute("aria-hidden", "true");

    clone.querySelectorAll("[data-shopify-editor-block]").forEach((element) => {
      element.removeAttribute("data-shopify-editor-block");
    });
    clone
      .querySelectorAll("a, button, input, select, textarea")
      .forEach((element) => {
        element.tabIndex = -1;
      });

    return clone;
  }

  removeClones() {
    this.track
      .querySelectorAll("[data-logo-ticker-clone]")
      .forEach((clone) => clone.remove());
  }

  scheduleMeasure() {
    cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(() => this.measure());
  }

  measure() {
    this.stopAnimation();
    this.removeClones();
    this.track.style.transform = "translate3d(0, 0, 0)";
    this.offset = 0;

    if (this.reducedMotion.matches) {
      this.dataset.reducedMotion = "true";
      this.dataset.ready = "true";
      return;
    }

    delete this.dataset.reducedMotion;
    this.groupWidth = this.originalGroup.getBoundingClientRect().width;

    if (!this.groupWidth) return;

    this.track.insertBefore(this.createClone(), this.originalGroup);

    const requiredWidth = this.viewport.clientWidth + this.groupWidth;
    while (this.track.scrollWidth < requiredWidth) {
      this.track.appendChild(this.createClone());
    }

    this.render();
    this.dataset.ready = "true";
    this.updateAnimation();
  }

  render() {
    this.track.style.transform = `translate3d(${
      this.offset - this.groupWidth
    }px, 0, 0)`;
  }

  handleFrame(time) {
    const elapsed = Math.min((time - this.lastFrameTime) / 1000, 0.1);
    this.lastFrameTime = time;
    this.offset = (this.offset + this.speed * elapsed) % this.groupWidth;
    this.render();
    this.animationFrame = requestAnimationFrame(this.handleFrame);
  }

  shouldAnimate() {
    return (
      !this.reducedMotion.matches &&
      this.isVisible &&
      document.visibilityState === "visible" &&
      !this.hasFocus &&
      !(this.pauseOnHover && this.isHovered) &&
      this.groupWidth > 0
    );
  }

  updateAnimation() {
    if (!this.shouldAnimate()) {
      this.stopAnimation();
      return;
    }

    if (this.animationFrame !== null) return;
    this.lastFrameTime = performance.now();
    this.animationFrame = requestAnimationFrame(this.handleFrame);
  }

  stopAnimation() {
    if (this.animationFrame === null) return;
    cancelAnimationFrame(this.animationFrame);
    this.animationFrame = null;
  }

  handleVisibilityChange() {
    this.updateAnimation();
  }
}

if (!customElements.get("logo-ticker")) {
  customElements.define("logo-ticker", LogoTicker);
}
